import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Switch } from './ui/switch';
import { X, Settings } from 'lucide-react';

const CookieBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  const [preferences, setPreferences] = useState({
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    const rawConsent = localStorage.getItem('cookieConsent');
    if (!rawConsent) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    } else if (rawConsent !== 'false' && rawConsent !== 'true') {
      try {
        setPreferences(JSON.parse(rawConsent));
      } catch(e) {}
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'analytics_storage': prefs.analytics ? 'granted' : 'denied'
      });
    }
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
  };

  const handleRejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
  };
  
  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!isVisible && !showSettings) return null;

  const content = {
    en: {
      message: "We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking 'Accept All', you consent to our use of cookies.",
      accept: "Accept All",
      decline: "Reject All",
      manage: "Manage Preferences",
      settingsTitle: "Privacy Preference Center",
      strictlyNecessary: "Strictly Necessary Cookies",
      strictlyDesc: "These cookies are required for the website to function and cannot be switched off.",
      analytics: "Analytics Cookies",
      analyticsDesc: "These allow us to count visits and traffic sources so we can measure and improve the performance of our site.",
      marketing: "Marketing Cookies",
      marketingDesc: "These cookies may be set through our site by our advertising partners to build a profile of your interests.",
      savePrefs: "Save Preferences"
    },
    id: {
      message: "Kami menggunakan cookie untuk meningkatkan pengalaman penelusuran Anda, menyajikan konten yang dipersonalisasi, dan menganalisis lalu lintas kami. Dengan mengklik 'Terima Semua', Anda menyetujui penggunaan cookie kami.",
      accept: "Terima Semua",
      decline: "Tolak Semua",
      manage: "Kelola Preferensi",
      settingsTitle: "Pusat Preferensi Privasi",
      strictlyNecessary: "Cookie yang Sangat Diperlukan",
      strictlyDesc: "Cookie ini diperlukan agar situs web berfungsi dan tidak dapat dimatikan.",
      analytics: "Cookie Analitik",
      analyticsDesc: "Ini memungkinkan kami menghitung kunjungan dan sumber lalu lintas untuk mengukur dan meningkatkan kinerja situs kami.",
      marketing: "Cookie Pemasaran",
      marketingDesc: "Cookie ini mungkin dipasang melalui situs kami oleh mitra periklanan kami untuk membangun profil minat Anda.",
      savePrefs: "Simpan Preferensi"
    }
  };

  const text = content[language] || content.en;

  return (
    <>
      {isVisible && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-800 shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.1)]">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-8">
            <div className="text-sm text-gray-600 dark:text-gray-300 flex-1 text-center lg:text-left">
              {text.message}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0 justify-center">
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 dark:text-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200"
              >
                <Settings className="w-4 h-4" />
                {text.manage}
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-200"
              >
                {text.decline}
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 shadow-sm"
              >
                {text.accept}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{text.settingsTitle}</h2>
              <button 
                onClick={() => {
                  setShowSettings(false);
                  if(!localStorage.getItem('cookieConsent')) setIsVisible(true);
                }} 
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Strictly Necessary */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{text.strictlyNecessary}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{text.strictlyDesc}</p>
                </div>
                <div className="pt-1">
                  <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Always Active</span>
                </div>
              </div>
              
              <div className="h-px bg-gray-100 dark:bg-slate-800" />
              
              {/* Analytics */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{text.analytics}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{text.analyticsDesc}</p>
                </div>
                <div className="pt-1">
                  <Switch 
                    checked={preferences.analytics} 
                    onCheckedChange={(c) => setPreferences({...preferences, analytics: c})} 
                  />
                </div>
              </div>
              
              <div className="h-px bg-gray-100 dark:bg-slate-800" />
              
              {/* Marketing */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{text.marketing}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{text.marketingDesc}</p>
                </div>
                <div className="pt-1">
                  <Switch 
                    checked={preferences.marketing} 
                    onCheckedChange={(c) => setPreferences({...preferences, marketing: c})} 
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={handleRejectAll}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-100 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700 rounded-lg transition-colors"
              >
                {text.decline}
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors shadow-sm"
              >
                {text.savePrefs}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
